import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Grid,
  User,
  Mail,
  Phone,
  Home,
  UserCheck,
  Edit,
  Briefcase,
  LogOut,
  Settings,
  Menu,
  X,
} from "lucide-react";
import secureLocalStorage from "react-secure-storage";

export default function AnimatedSidebar({
  logoColor = "#1B2A6D", // matching IIUC logo color
  user = {
    name: secureLocalStorage.getItem("name"),
    phone: secureLocalStorage.getItem("mobile_number"),
    email: secureLocalStorage.getItem("user"),
    avatarUrl: secureLocalStorage.getItem("picture") || null,
  },
}) {
  const [open, setOpen] = useState(true);

  const menu = [
    { id: "dashboard", label: "Dashboard", icon: Home, link:"/dashboard" },
    { id: "view-profile", label: "View Profile", icon: User, link:"/dashboard/view-profile" },
    { id: "update-profile", label: "Update Profile", icon: Edit,link:"/dashboard/update-profile" },
    { id: "applied-jobs", label: "Applied Jobs", icon: Briefcase, link:"/dashboard/applied-jobs" },
  ];

  const bottom = [
    { id: "logout", label: "Logout", icon: LogOut, link:"/logout" },
    { id: "settings", label: "Basic Settings", icon: Settings, link:"/dashboard/settings" },
  ];

  // Automatically slim the sidebar when screen size is mobile
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setOpen(false);
      } else {
        setOpen(true);
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <aside className="flex h-screen">
      {open && window.innerWidth < 768 && (
        <motion.div
          initial={false}
          animate={{ opacity: open ? 0.5 : 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 bg-black z-10 md:hidden"
          onClick={() => setOpen(false)}
        />
      )}

      <motion.nav
        initial={false}
        animate={{ width: open ? 280 : 64 }}
        transition={{ type: "spring", stiffness: 200, damping: 30 }}
        className="z-20 fixed left-0 top-0 bottom-0 rounded-none shadow-2xl text-white overflow-hidden md:static md:ml-0 md:my-0"
        style={{
          background: `linear-gradient(180deg, ${logoColor} 0%, ${logoColor}e6 100%)`,
          height: "100vh",
        }}
      >
        {/* decorative SVG header */}
        <div className="relative h-36 flex items-center px-2 bg-white/10 backdrop-blur-sm border-b border-white/20">
          <svg viewBox="0 0 200 60" className="absolute inset-0 w-full h-full" preserveAspectRatio="none" aria-hidden>
            <path d="M0,40 C40,60 80,0 140,30 C180,50 200,20 200,20 L200,60 L0,60 Z" fill="white" fillOpacity="0.08" />
          </svg>
			<button
              aria-label={open ? "Collapse sidebar" : "Expand sidebar"}
              onClick={() => setOpen((s) => !s)}
              className="ml-auto p-0 rounded-lg hover:bg-white/20 active:scale-95 transition absolute top-0 right-3 z-30"
            >
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
			{open ? (
          <div className="relative flex items-center gap-3 w-full">
			
			
            <div className="flex items-center gap-3">
              <div className="rounded-full ring-2 ring-white p-0.5 bg-white/70">
                {user.avatarUrl ? (
                  <img src={`${import.meta.env.VITE_API_SOURCE_URL}${user.avatarUrl}`} alt="avatar" className="w-full h-12 rounded-full object-cover" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-white to-slate-100 flex items-center justify-center text-xl font-semibold text-slate-700">{(user.name || "U").slice(0, 1)}</div>
                )}
              </div>
            </div>
			
			
            <div className={`flex-1 transition-opacity duration-200 ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`}>
              <div className="font-semibold leading-tight">{user.name}</div>
              <div className="text-sm flex gap-2 items-center mt-1 opacity-90">
                <Phone size={14} />
                <span>{user.phone}</span>
              </div>
              <div className="text-sm flex gap-2 items-center mt-1 opacity-90">
                <Mail size={14} />
                <span className="truncate">{user.email}</span>
              </div>
            </div>

            {/* collapse/expand button always visible */}
            
          </div>
		  ):(
		  <div className="relative md:flex items-center gap-3 w-full">
			
			
            <div className="md:flex items-center gap-3 mt-20 md:mt-10">
              <div className="rounded-full ring-2 ring-white p-0.5 bg-white/70">
                {user.avatarUrl ? (
                  <img src={`${import.meta.env.VITE_API_SOURCE_URL}${user.avatarUrl}`} alt="avatar" className="w-full h-12 rounded-full object-cover" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-white to-slate-100 flex items-center justify-center text-xl font-semibold text-slate-700">{(user.name || "U").slice(0, 1)}</div>
                )}
              </div>
            </div>
			
			
            <div className={`flex-1 transition-opacity duration-200 ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`}>
              <div className="font-semibold leading-tight">{user.name}</div>
              <div className="text-sm flex gap-2 items-center mt-1 opacity-90">
                <Phone size={14} />
                <span>{user.phone}</span>
              </div>
              <div className="text-sm flex gap-2 items-center mt-1 opacity-90">
                <Mail size={14} />
                <span className="truncate">{user.email}</span>
              </div>
            </div>

            {/* collapse/expand button always visible */}
            
          </div>
			)}
        </div>

        {/* Main content */}
        <div className="flex flex-col h-[calc(100%-9rem)]">
          <div className="flex-1 px-2 py-4 overflow-auto">
            <nav className="flex flex-col gap-1">
              {menu.map((m) => {
                const Icon = m.icon;
                return (
                  <motion.a
                    key={m.id}
                    whileHover={{ x: 6 }}
                    className={`group flex items-center gap-3 rounded-lg px-3 py-2 cursor-pointer hover:bg-white/10 transition-colors`}
                    href={m.link}
                    onClick={() => window.innerWidth < 768 && setOpen(false)}
                  >
                    <div className={`flex items-center justify-center w-10 h-10 rounded-lg ${open ? "" : "mx-auto"} bg-white/20`}>
                      <Icon size={18} className="text-white" />
                    </div>
                    <div className={`flex-1 font-medium ${open ? "ml-1" : "hidden"}`}>{m.label}</div>
                  </motion.a>
                );
              })}
            </nav>
          </div>

          <div className="px-3 pb-4">
            <div className="border-t border-white/20 pt-4">
              <div className="flex flex-col gap-2">
                {bottom.map((b) => {
                  const Icon = b.icon;
                  return (
                    <a key={b.id} href={b.link} className="group flex items-center gap-3 rounded-lg px-3 py-2 hover:bg-white/10 transition-colors" onClick={() => window.innerWidth < 768 && setOpen(false)}>
                      <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-white/20">
                        <Icon size={18} />
                      </div>
                      <div className={`${open ? "block" : "hidden"} font-medium`}>{b.label}</div>
                    </a>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </motion.nav>

      <main className="flex-1 ml-0 md:ml-0 w-full"></main>
    </aside>
  );
}